import React, { useCallback, useState } from 'react';
import { DataTable, DataTableProps, DataTableSortEvent } from 'primereact/datatable';
import { Column, ColumnProps } from 'primereact/column';
import { Skeleton } from 'primereact/skeleton';
import { Message } from 'primereact/message';
import { Button } from 'primereact/button';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'localization';
import type { PaginatorTemplate } from 'primereact/paginator';
import DropdownAtom from '../Dropdown/DropdownAtom';
import './DataGridAtom.scss';

/**
 * PrimeReact makes every DataTable wrapper `overflow: auto`. A loading skeleton is a non-interactive
 * placeholder with nothing focusable in it, so when its columns' min-widths exceed the container it
 * becomes a keyboard-unreachable scroll region (axe `scrollable-region-focusable`). The placeholder
 * is clipped instead; the loaded table, whose rows hold focusable cells, keeps scrolling.
 */
const SKELETON_PASS_THROUGH = { wrapper: { style: { overflow: 'hidden' } } };

type RowsPerPageDropdownOptions = Parameters<
  Extract<NonNullable<NonNullable<Exclude<PaginatorTemplate, string>>['RowsPerPageDropdown']>, (...args: never[]) => unknown>
>[0];

export interface DataGridColumn extends Omit<ColumnProps, 'body'> {
  field: string;
  header: string | React.ReactNode;
  body?: (rowData: any, options: any) => React.ReactNode;
  sortable?: boolean;
  filterable?: boolean;
  width?: string;
  minWidth?: string;
  /** If true, values in this column are sphere IDs - DataListMolecule will batch-fetch and render with SphereIdentityAtom */
  isSphereId?: boolean;
  /** Type of inline column filter to render in the column header */
  filterType?: 'text' | 'enum' | 'date' | 'numeric' | 'perspective';
  /** Available options for enum/multi-select column filters */
  filterEnumValues?: Array<{ label: string; value: string }>;
  /** Custom placeholder text for the column filter input */
  filterPlaceholder?: string;
  /** Perspective system name for perspective-type column filters (e.g., 'center_responsible') */
  filterPerspectiveSystemName?: string;
  /** If true, text is truncated with ellipsis and full value shown on hover as a tooltip */
  truncateWithTooltip?: boolean;
}

export interface DataGridAction {
  id: string;
  label: string;
  icon?: string;
  severity?: 'secondary' | 'success' | 'info' | 'warning' | 'help' | 'danger';
  onClick: (rowData: any) => void;
  disabled?: (rowData: any) => boolean;
  visible?: (rowData: any) => boolean;
}

export interface DataGridAtomProps extends Omit<DataTableProps<any>, 'value' | 'columns'> {
  data: any[];
  columns: DataGridColumn[];
  loading?: boolean;
  error?: string | null;
  emptyMessage?: string;
  actions?: DataGridAction[];
  showActions?: boolean;
  globalFilterValue?: string;
  onSort?: (event: DataTableSortEvent) => void;
  sortField?: string;
  sortOrder?: 1 | -1;
  // Pagination props
  paginator?: boolean;
  rows?: number;
  rowsPerPageOptions?: number[];
  // Selection props - Updated to match PrimeReact DataTable types
  selectionMode?: 'single' | 'multiple' | 'checkbox' | null;
  selection?: any;
  onSelectionChange?: (e: { value: any }) => void;
  // Styling
  className?: string;
  tableStyle?: React.CSSProperties;
  // Header props
  headerTitle?: string;
  headerActions?: React.ReactNode;
  showHeader?: boolean;
  // Column resizing
  resizableColumns?: boolean;
  columnResizeMode?: 'fit' | 'expand';
}

const DataGridAtom: React.FC<DataGridAtomProps> = ({
  data,
  columns,
  loading = false,
  error = null,
  emptyMessage = 'No data available',
  actions = [],
  showActions = false,
  globalFilterValue,
  onSort,
  sortField,
  sortOrder,
  paginator = false,
  rows = 10,
  rowsPerPageOptions = [5, 10, 25],
  selectionMode,
  selection,
  onSelectionChange,
  className,
  tableStyle,
  headerTitle,
  headerActions,
  showHeader = true,
  resizableColumns = true,
  columnResizeMode = 'expand',
  ...dataTableProps
}) => {
  const { t } = useTranslation();
  const [localSortField, setLocalSortField] = useState<string | null>(sortField || null);
  const [localSortOrder, setLocalSortOrder] = useState<1 | -1>(sortOrder || 1);

  // PrimeReact's DataTable assumes `value` is an array (selection/sort helpers
  // call `.some`/`.every` on it). Guarantee that at the design-system boundary.
  const safeData = Array.isArray(data) ? data : [];

  // Handle sorting
  const handleSort = (event: DataTableSortEvent) => {
    setLocalSortField(event.sortField);
    setLocalSortOrder(event.sortOrder as 1 | -1);
    
    if (onSort) {
      onSort(event);
    }
  };

  // Render loading skeleton
  const renderSkeleton = () => {
    const skeletonRows = Array.from({ length: rows }, () => uuidv4());
    
    return (
      <DataTable
        value={skeletonRows.map(id => ({ id }))}
        className={`data-grid-skeleton ${className || ''}`}
        style={tableStyle}
        pt={SKELETON_PASS_THROUGH}
      >
        {columns.map((col) => (
          <Column
            key={col.field}
            field={col.field}
            header={col.header}
            style={{ width: col.width, minWidth: col.minWidth }}
            body={() => <Skeleton height="1.5rem" />}
          />
        ))}
        {showActions && actions.length > 0 && (
          <Column
            header="Actions"
            style={{ width: '120px' }}
            body={() => <Skeleton height="1.5rem" />}
          />
        )}
      </DataTable>
    );
  };

  // Render actions column body — memoized so PrimeReact does not re-render all rows on unrelated parent re-renders
  // NOTE: must be before early returns to satisfy React rules of hooks
  const actionsBody = useCallback((rowData: any) => {
    const visibleActions = actions.filter(action =>
      !action.visible || action.visible(rowData)
    );

    if (visibleActions.length === 0) {
      return null;
    }

    return (
      <div className="data-grid-actions">
        {visibleActions.map(action => (
          <Button
            key={action.id}
            label={action.label}
            icon={action.icon}
            size="small"
            severity={action.severity || 'secondary'}
            outlined
            onClick={() => action.onClick(rowData)}
            disabled={action.disabled ? action.disabled(rowData) : false}
            className="data-grid-action-button"
            type="button"
          />
        ))}
      </div>
    );
  }, [actions]);

  // PrimeReact's own rows-per-page dropdown ships a hidden native <select> with no accessible
  // name; DropdownAtom names the combobox, trigger and select from one translated label.
  const paginatorTemplate = {
    layout:
      'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport',
    RowsPerPageDropdown: (options: RowsPerPageDropdownOptions) => (
      <DropdownAtom
        value={options.value}
        options={options.options}
        onChange={options.onChange}
        appendTo={options.appendTo}
        disabled={options.disabled}
        ariaLabel={t('dataList.settings.rowsPerPage')}
      />
    ),
  };

  // Render error state
  if (error) {
    return (
      <div className="data-grid-error">
        <Message
          severity="info"
          text={error}
          className="w-full"
          icon="pi pi-info-circle"
        />
      </div>
    );
  }

  // Render loading state
  if (loading) {
    return (
      <div className={`data-grid-atom ${className || ''}`}>
        <div className="data-grid-loading">
          {renderSkeleton()}
        </div>
      </div>
    );
  }

  // Header template with title and actions
  const headerTemplate = (showHeader && (headerTitle || headerActions)) ? (
    <div className="data-grid-header">
      {headerTitle && (
        <h3 className="data-grid-title">{headerTitle}</h3>
      )}
      {headerActions && (
        <div className="data-grid-header-actions">
          {headerActions}
        </div>
      )}
    </div>
  ) : undefined;

  return (
    <div className={`data-grid-atom ${className || ''}`}>
      <DataTable
        value={safeData}
        header={headerTemplate}
        globalFilter={globalFilterValue}
        emptyMessage={emptyMessage}
        paginator={paginator}
        rows={rows}
        rowsPerPageOptions={rowsPerPageOptions}
        paginatorTemplate={paginatorTemplate}
        currentPageReportTemplate="Showing {first}–{last} of {totalRecords}"
        selectionMode={selectionMode || null}
        selection={selection}
        onSelectionChange={onSelectionChange}
        sortField={localSortField || undefined}
        sortOrder={localSortOrder}
        onSort={handleSort}
        stripedRows
        scrollable
        resizableColumns={resizableColumns}
        columnResizeMode={columnResizeMode}
        style={tableStyle}
        className="p-datatable-gridlines data-grid-table"
        {...dataTableProps}
      >
        {columns.map((col) => {
          // Extract field and header to prevent duplication
          const { field, header, ...otherColProps } = col;
          return (
            <Column
              key={field}
              field={field}
              header={header}
              style={{ width: col.width, minWidth: col.minWidth }}
              {...otherColProps}
            />
          );
        })}
        {showActions && actions.length > 0 && (
          <Column
            header="Actions"
            body={actionsBody}
            style={{ width: '120px', minWidth: '120px' }}
            className="data-grid-actions-column"
          />
        )}
      </DataTable>
    </div>
  );
};

export default DataGridAtom;